import { useCallback } from "react"
import { graphql, UseMutationConfig } from "react-relay"
import { type CustomMutationParams, useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import { getDiagnosisCodeByDiagnosisCode } from "@/modules/patients/detail/therapy-prescription/ch/services/getDiagnosisCodeByDiagnosisCode"
import { useDeGenerateTherapyPrescriptionDocumentMutation as useDeGenerateTherapyPrescriptionDocumentMutationType } from "@/modules/patients/detail/therapy-prescription/de/services/__generated__/useDeGenerateTherapyPrescriptionDocumentMutation.graphql"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

const mutation = graphql`
  mutation useDeGenerateTherapyPrescriptionDocumentMutation($input: De_generateTherapyPrescriptionDocumentInput!) {
    de_generateTherapyPrescriptionDocument(input: $input) {
      de_TherapyPrescription {
        id
        fileId
        ...deTherapyPrescriptionFragment
      }
      errors {
        ... on Error {
          message
        }
      }
    }
  }
`

type Config = Omit<UseMutationConfig<useDeGenerateTherapyPrescriptionDocumentMutationType>, "variables"> & {
  id: string
  diagnosisCode: string
}

const useDeGenerateTherapyPrescriptionDocumentMutation = (params?: CustomMutationParams) => {
  const [commit, isFlight] = useMutationWithErrorDialog<useDeGenerateTherapyPrescriptionDocumentMutationType>(
    mutation,
    params,
  )
  const patientById = usePatientParam()

  const commitHandler = useCallback(
    async (config: Config) => {
      const { code } = await getDiagnosisCodeByDiagnosisCode(config.diagnosisCode)

      commit({
        variables: {
          input: { id: config.id, patientId: patientById, diagnosisCode: code },
        },
        ...config,
      })
    },
    [commit, patientById],
  )

  return [commitHandler, isFlight] as const
}

export { useDeGenerateTherapyPrescriptionDocumentMutation }
